import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";

export interface SlackUser {
  id: string;
  name: string;
  real_name?: string;
  is_bot?: boolean;
  is_admin?: boolean;
  deleted?: boolean;
  updated?: number;
  profile?: {
    display_name?: string;
    real_name?: string;
    image_24?: string;
    image_32?: string;
    image_48?: string;
    image_72?: string;
  };
}

function isSlackUser(value: unknown): value is SlackUser {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as SlackUser).id === "string" &&
    typeof (value as SlackUser).name === "string"
  );
}

const MAX_PAGES = 5;

/**
 * Fetch Slack workspace members via the slack-list-users action test endpoint.
 * Mirrors fetchSlackChannels — paginates, dedupes, and excludes bots/deleted.
 */
export async function fetchSlackUsers(connectorId: string, connectorType: string): Promise<SlackUser[]> {
  const allUsers: SlackUser[] = [];
  let cursor: string | undefined;

  for (let page = 0; page < MAX_PAGES; page++) {
    const inputs: Record<string, unknown> = { limit: 200 };
    if (cursor) inputs.cursor = cursor;

    const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/actions/slack-list-users/test`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        inputs,
        connector: { id: connectorId, type: connectorType },
      }),
    });

    if (!response.ok) {
      if (allUsers.length === 0) throw new Error(`Failed to fetch Slack users (HTTP ${response.status})`);
      console.warn(`[SlackUsers] Pagination stopped at page ${page + 1}: HTTP ${response.status}`);
      break;
    }

    const data = await response.json();
    const ret = data?.data?.result?.data?.ret;
    if (!ret || typeof ret !== "object") {
      console.warn("[SlackUsers] Unexpected response shape", { page: page + 1, keys: Object.keys(data ?? {}) });
      break;
    }

    const { members, response_metadata } = ret;
    if (Array.isArray(members)) {
      allUsers.push(...members.filter(isSlackUser));
    }

    const nextCursor = response_metadata?.next_cursor as string | undefined;
    if (!nextCursor) break;
    cursor = nextCursor;
  }

  const seen = new Set<string>();
  const uniqueUsers: SlackUser[] = [];
  for (const u of allUsers) {
    if (!seen.has(u.id) && !u.deleted && !u.is_bot && u.id !== "USLACKBOT") {
      seen.add(u.id);
      uniqueUsers.push(u);
    }
  }
  return uniqueUsers;
}

/** Resolve a Slack user id (e.g. "U0855RQ3YQN") to its display name. */
export function resolveSlackUserLabel(value: string, usersById?: Map<string, SlackUser>): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("@")) return trimmed;

  const user = usersById?.get(trimmed);
  if (user) {
    return user.profile?.display_name || user.real_name || user.name;
  }

  // Hide raw Slack user IDs when we can't resolve them.
  if (/^[UW][A-Z0-9]+$/i.test(trimmed)) {
    return "Slack user";
  }

  return trimmed;
}

/** Resolve a Slack user id to its avatar URL (48px), if available. */
export function resolveSlackUserAvatar(value: string, usersById?: Map<string, SlackUser>): string | null {
  const user = usersById?.get(value.trim());
  return user?.profile?.image_48 || user?.profile?.image_32 || user?.profile?.image_24 || null;
}
